import { useState } from "react"
import { useQuery, useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { Clock3 } from "lucide-react"
import { CAMPAIGN_STATUS_LABELS } from "@vienna/contracts"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Separator } from "@/components/ui/separator"
import { Header, Hint, Section, SelectField, Status } from "@/components/audience/primitives"
import { providerLabel } from "@/components/campaigns/helpers"
import { BackLink, Footer, Steps } from "@/components/campaigns/primitives"
import { formatDateTime } from "@/lib/format"
import { campaignEstimateQuery, campaignQuery } from "@/lib/queries"
import { useWorkspaceContext } from "@/lib/session"
import { useCampaignUpdate } from "./w.$workspace.campaigns.$campaignId.audience"

export const Route = createFileRoute("/w/$workspace/campaigns/$campaignId/schedule")({ component: CampaignSchedule })

const ZONES = ["UTC", "Europe/London", "Europe/Berlin", "Europe/Paris", "America/New_York", "America/Chicago", "America/Los_Angeles", "Asia/Singapore", "Asia/Tokyo", "Australia/Sydney"]

export function zoneOptions(current: string) {
  const browser = Intl.DateTimeFormat().resolvedOptions().timeZone
  const all = Array.from(new Set([current, browser, ...ZONES].filter(Boolean)))
  return all.map((z) => ({ value: z, label: z.replace(/_/g, " ") }))
}

/** Local wall-clock parts of a UTC instant in a zone, for editing an already-chosen time. */
export function wallClock(iso: string | null, timeZone: string): { date: string; time: string } {
  const d = iso ? new Date(iso) : new Date(Date.now() + 24 * 60 * 60 * 1000)
  const parts = new Intl.DateTimeFormat("en-GB", { timeZone, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }).formatToParts(d)
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "00"
  return { date: `${get("year")}-${get("month")}-${get("day")}`, time: iso ? `${get("hour")}:${get("minute")}` : "10:00" }
}

/** Screen 52. */
function CampaignSchedule() {
  const { workspace } = useWorkspaceContext()
  const { campaignId } = Route.useParams()
  const navigate = useNavigate()
  const campaign = useSuspenseQuery(campaignQuery(workspace.slug, campaignId)).data
  const estimate = useQuery({ ...campaignEstimateQuery(workspace.slug, campaignId), enabled: Boolean(campaign.segmentId && campaign.connectionId) })
  const [timing, setTiming] = useState<"now" | "scheduled">(campaign.timing)
  const [zone, setZone] = useState(campaign.timezone === "UTC" ? Intl.DateTimeFormat().resolvedOptions().timeZone : campaign.timezone)
  const initial = wallClock(campaign.scheduledAt, campaign.timezone)
  const [date, setDate] = useState(initial.date)
  const [time, setTime] = useState(initial.time)
  const update = useCampaignUpdate(campaignId)
  const params = { workspace: workspace.slug, campaignId }
  const editable = campaign.status === "draft"
  return (
    <div className="page-wrap au-root">
      <Header title={campaign.name} back={<BackLink to="/w/$workspace/campaigns" params={{ workspace: workspace.slug }}>Campaigns</BackLink>}>
        <Status>{CAMPAIGN_STATUS_LABELS[campaign.status]}</Status>
      </Header>
      <Steps current="schedule" workspace={workspace.slug} campaignId={campaignId} />
      <div className="au-two-column">
        <Section title="Choose a send time" description="Send once, at the right moment for your audience.">
          <div className="space-y-4">
            {(
              [
                ["now", "Send after review", "Start delivery once you confirm the campaign."],
                ["scheduled", "Schedule for later", "Choose a date and time for all recipients."],
              ] as const
            ).map(([value, label, detail]) => (
              <button key={value} className={`au-choice ${timing === value ? "selected" : ""}`} aria-pressed={timing === value} disabled={!editable} onClick={() => setTiming(value)}>
                <span className="au-radio">{timing === value && <span />}</span>
                <span>
                  <strong className="block text-sm font-medium">{label}</strong>
                  <span className="text-xs text-muted-foreground">{detail}</span>
                </span>
              </button>
            ))}
            <div className={`space-y-5 pt-4 ${timing === "now" ? "opacity-45" : ""}`}>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="send-date">Date</Label>
                  <Input id="send-date" type="date" value={date} disabled={timing === "now" || !editable} onChange={(e) => setDate(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="send-time">Time</Label>
                  <Input id="send-time" type="time" value={time} disabled={timing === "now" || !editable} onChange={(e) => setTime(e.target.value)} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Time zone</Label>
                <SelectField label="Time zone" value={zone} onChange={setZone} options={zoneOptions(zone)} className="w-full" />
              </div>
            </div>
            <Separator />
            <Hint icon="clock">The audience is frozen when you confirm and every recipient is checked again at the send time. Messaging windows may close before delivery starts.</Hint>
          </div>
        </Section>
        <Section title="Delivery summary">
          <dl className="au-definition">
            <div>
              <dt>Campaign</dt>
              <dd>{campaign.name}</dd>
            </div>
            <div>
              <dt>Channel</dt>
              <dd>{providerLabel(campaign.provider)}</dd>
            </div>
            <div>
              <dt>Audience</dt>
              <dd>{campaign.segmentName ?? "Not chosen"}</dd>
            </div>
            <div>
              <dt>Current estimate</dt>
              <dd>{estimate.data ? `${estimate.data.eligible.toLocaleString()} eligible people` : "—"}</dd>
            </div>
          </dl>
          <Separator className="my-5" />
          <div className="flex gap-3">
            <Clock3 className="mt-0.5 size-5" />
            <div>
              <p className="font-medium">{timing === "now" ? "After your final review" : `${date} at ${time}`}</p>
              <p className="mt-1 text-xs text-muted-foreground">{timing === "now" ? "You’ll confirm before delivery begins." : zone}</p>
            </div>
          </div>
          <p className="mt-6 text-xs leading-5 text-muted-foreground">If the connection needs attention, delivery pauses for you. Reschedule or cancel a scheduled campaign before it starts.</p>
          {campaign.scheduledAt && campaign.status !== "draft" && <p className="mt-2 text-xs text-muted-foreground">Scheduled for {formatDateTime(campaign.scheduledAt)}.</p>}
        </Section>
      </div>
      <Footer
        back={<BackLink to="/w/$workspace/campaigns/$campaignId/message" params={params}>Back</BackLink>}
        label="Continue to review"
        disabled={!editable || (timing === "scheduled" && (!date || !time))}
        pending={update.isPending}
        onNext={async () => {
          await update.mutateAsync({ schedule: timing === "scheduled" ? { timing, date, time, timezone: zone } : { timing, timezone: zone } })
          await navigate({ to: "/w/$workspace/campaigns/$campaignId/review", params })
        }}
      />
    </div>
  )
}
